import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

function Products() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ products: 0, upgradePaths: 0, bundles: 0, bonuses: 0 });
  const [loading, setLoading] = useState(true);
  const [retentionData, setRetentionData] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [productsRes, pathsRes, bundlesRes, bonusesRes, retentionRes] = await Promise.all([
        fetch('/api/products/get-catalog', { credentials: 'include' }),
        fetch('/api/upgrade-paths/get', { credentials: 'include' }),
        fetch('/api/bundles', { credentials: 'include' }),
        fetch('/api/bonuses', { credentials: 'include' }),
        fetch('/api/marketing/get-retention', { credentials: 'include' }),
      ]);

      if (productsRes.ok) {
        const data = await productsRes.json();
        setStats(prev => ({ ...prev, products: data.catalog?.length || 0 }));
      }
      if (pathsRes.ok) {
        const data = await pathsRes.json();
        setStats(prev => ({ ...prev, upgradePaths: data.upgrade_paths?.length || 0 }));
      }
      if (bundlesRes.ok) {
        const data = await bundlesRes.json();
        setStats(prev => ({ ...prev, bundles: data.bundles?.length || 0 }));
      }
      if (bonusesRes.ok) {
        const data = await bonusesRes.json();
        setStats(prev => ({ ...prev, bonuses: data.bonuses?.length || 0 }));
      }
      if (retentionRes.ok) {
        const data = await retentionRes.json();
        setRetentionData(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const sectionGroups = [
    {
      label: 'Catalog',
      items: [
        {
          id: 'catalog',
          title: 'Product Catalog',
          icon: '📦',
          description: 'Manage your products, pricing, and value ladder.',
          path: '/command-center/products/catalog',
          count: stats.products,
          countLabel: 'products',
        },
        {
          id: 'upgrade-paths',
          title: 'Upgrade Paths',
          icon: '🔄',
          description: 'Define how customers move through your product ladder.',
          path: '/command-center/products/upgrade-paths',
          count: stats.upgradePaths,
          countLabel: 'paths',
        },
      ],
    },
    {
      label: 'Offer Building',
      items: [
        {
          id: 'bundles',
          title: 'Bundles',
          icon: '📦',
          description: 'Combine multiple products into value-packed bundles.',
          path: '/command-center/products/bundles',
          count: stats.bundles,
          countLabel: 'bundles',
        },
        {
          id: 'bonuses',
          title: 'Bonuses',
          icon: '🎀',
          description: 'Value-add items that sweeten your offers and promotions.',
          path: '/command-center/products/bonuses',
          count: stats.bonuses,
          countLabel: 'bonuses',
        },
      ],
    },
  ];

  const cardStyle = {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `1px solid ${colors.greyLight}`,
    height: '100%',
  };

  const iconStyle = {
    fontSize: '2rem',
    marginBottom: '0.5rem',
  };

  const titleStyle = {
    color: colors.greyDarkest,
    fontWeight: '600',
    fontSize: '1rem',
    marginBottom: '0.5rem',
  };

  return (
    <Container className="py-4">
      <div className="mb-4">
        <h1 className="mb-2">Products</h1>
        <p className="text-muted mb-0">
          Your products are the foundation of your business. Manage your catalog, define how customers
          move through your value ladder, and build irresistible offers.
        </p>
      </div>

      {/* Stats Summary */}
      {!loading && (
        <Row className="mb-4">
          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center h-100 border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: colors.goldMid }}>
                  {stats.products}
                </div>
                <div className="text-muted">Products in Catalog</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center h-100 border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#28a745' }}>
                  {stats.upgradePaths}
                </div>
                <div className="text-muted">Upgrade Paths</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center h-100 border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#6f42c1' }}>
                  {stats.bundles}
                </div>
                <div className="text-muted">Bundles</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} lg={3} className="mb-3">
            <Card className="text-center h-100 border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#e83e8c' }}>
                  {stats.bonuses}
                </div>
                <div className="text-muted">Bonuses</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Grouped Cards */}
      {sectionGroups.map((group) => (
        <div key={group.label} className="mb-4">
          <h5 style={{
            color: colors.greyDarkest,
            fontWeight: 600,
            fontSize: '0.95rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '0.75rem',
            paddingBottom: '0.5rem',
            borderBottom: `2px solid ${colors.goldDark}`,
          }}>
            {group.label}
          </h5>
          <Row>
            {group.items.map((section) => (
              <Col key={section.id} md={6} className="mb-3">
                <Card
                  className="h-100 shadow-sm"
                  style={{
                    border: `1px solid ${colors.greyLight}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={() => navigate(section.path)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.goldMid;
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.greyLight;
                    e.currentTarget.style.boxShadow = '0 .125rem .25rem rgba(0,0,0,.075)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Card.Header style={{
                    backgroundColor: colors.greyDarkest,
                    color: colors.goldLight,
                    fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                    letterSpacing: '0.1em',
                  }}>
                    <strong>{section.title}</strong>
                  </Card.Header>
                  <Card.Body className="d-flex flex-column p-4">
                    <p className="text-muted flex-grow-1">{section.description}</p>
                    {section.count != null && !loading && (
                      <Badge bg="light" text="dark" className="mb-3" style={{ alignSelf: 'flex-start' }}>
                        {section.count} {section.countLabel}
                      </Badge>
                    )}
                    <Button
                      variant="outline-dark"
                      className="mt-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(section.path);
                      }}
                    >
                      Open {section.title} →
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ))}

      {/* Upsell/Cross-sell Strategy Section */}
      {retentionData && (retentionData.has_upsells || retentionData.upsell_strategy) && (
        <Card className="shadow-sm" style={{ borderLeft: `4px solid ${colors.goldMid}` }}>
          <Card.Header style={{ backgroundColor: colors.greyLightest }}>
            <div className="d-flex justify-content-between align-items-center">
              <strong>Your Upsell & Cross-sell Strategy</strong>
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate('/client-retention')}
              >
                Edit Strategy
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            {retentionData.upsell_strategy ? (
              <div>
                <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>{retentionData.upsell_strategy}</p>
                <small className="text-muted">
                  This strategy from your Client Retention planning informs how products connect in your funnels.
                </small>
              </div>
            ) : (retentionData.has_upsells === 'yes-active' || retentionData.has_upsells === 'yes-available') ? (
              <div>
                <Badge bg="success" className="mb-2">Upsells Enabled</Badge>
                <p className="text-muted mb-0">
                  You've indicated you have upsells but haven't documented your strategy yet.
                  <Button
                    variant="link"
                    className="p-0 ms-1"
                    onClick={() => navigate('/client-retention')}
                  >
                    Add details →
                  </Button>
                </p>
              </div>
            ) : null}
          </Card.Body>
        </Card>
      )}

      {/* Help Section */}
      <Card className="mt-4 border-0 shadow-sm" style={{ backgroundColor: colors.greyLightest }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={8}>
              <h5 className="mb-2">Need help with your product strategy?</h5>
              <p className="text-muted mb-0">
                The Business Strategist can help you analyze your product lineup, identify gaps,
                and optimize your value ladder for maximum revenue.
              </p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              <Button
                variant="outline-dark"
                onClick={() => navigate('/command-center/profit-advisor')}
              >
                Ask Business Strategist
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
}

export default Products;
